/* eslint-disable complexity */
import React, { useState, createContext, ReactNode } from "react";

import { css } from "@compiled/react";
import { useInView } from "react-intersection-observer";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import UFOSegment from "@atlassian/wac-ufo-segment";

import { Adg4Button } from "@/components/adg4/button/src";
import { Video } from "@/components/wpl/video/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import { Row } from "@/libraries/wpl/foundations/src";

import {
    LogoWhiteJira,
    LogoWhiteConfluence,
    LogoWhiteJiraServiceManagement,
    LogoWhiteLoom,
    LogoWhiteJiraProductDiscovery
} from "./illustrations";
import { Sparkles } from "./illustrations";
import { HomepageSectionScrollItem } from "./scroll-item";
export type ScrollContextType = {
    activeAccentImg: string | null;
    activeBrand: string | null;
    activeColor: string | null;
    activeCTA: string | null;
    activeCTALink: string | null;
    activeCTALabel: string | null;
    activeFeatureVideo: string | null;
    activePosterImage: string | null;
    activeIndex: number | 0;
    activeShape: string | null;
    setActiveAccentImg: (newValue: string) => void;
    setActiveBrand: (newValue: string) => void;
    setActiveColor: (newValue: string) => void;
    setActiveCTA: (newValue: string) => void;
    setActiveCTALink: (newValue: string) => void;
    setActiveCTALabel: (newValue: string) => void;
    setActiveFeatureVideo: (newValue: string) => void;
    setActivePosterImage: (newValue: string) => void;
    setActiveIndex: (newValue: number) => void;
    setActiveShape: React.Dispatch<React.SetStateAction<string | null>>;
};
const ScrollContextState = {
    activeAccentImg: null,
    activeBrand: null,
    activeColor: null,
    activeCTA: null,
    activeCTALink: null,
    activeCTALabel: null,
    activeFeatureVideo: null,
    activePosterImage: null,
    activeIndex: 0,
    activeShape: null,
    setActiveAccentImg: () => undefined,
    setActiveBrand: () => undefined,
    setActiveColor: () => undefined,
    setActiveCTA: () => undefined,
    setActiveCTALink: () => undefined,
    setActiveCTALabel: () => undefined,
    setActiveFeatureVideo: () => undefined,
    setActivePosterImage: () => undefined,
    setActiveIndex: () => 0,
    setActiveShape: () => undefined
};
export const ScrollContext =
    createContext<ScrollContextType>(ScrollContextState);
export type TeamType = {
    index?: number;
    productName: string;
    logo: ReactNode;
    logoBGColor?: string;
    cta: string;
    ctaLink: string;
    ctaLabel: string;
    header: string;
    description: string;
    quote: string;
    brand: string;
    name: string;
    title: string;
    portrait: string;
    featureVideo: string;
    posterImage: string;
    bgColor: string;
    bgShape: string;
    accentImg: string;
};
export const ScrollPinnedSections = () => {
    const intl = useIntl();
    const Teams: TeamType[] = [
        {
            productName: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Jira.ProductName",
                defaultMessage: "Jira"
            }),
            logo: <LogoWhiteJira />,
            cta: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Jira.Cta",
                defaultMessage: "Get started with Jira"
            }),
            ctaLink: useLocalizedHref("/software/jira"),
            ctaLabel: "Get started with Jira",
            header: "Dream it, plan it, launch it",
            description: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Jira.Description",
                defaultMessage:
                    "The #1 tool for agile teams is now for all teams. Plan, track, and deliver your biggest ideas together."
            }),

            quote: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Jira.Quote",
                defaultMessage:
                    "The pace of delivery from idea to reality has exploded."
            }),
            name: "Greg Franklin",
            // TODO: localize title
            title: "Agile Lead, Cancer Research UK",
            brand: "https://wac-cdn.atlassian.com/misc-assets/webp-images/scroll-teams-brand-1.webp",
            portrait:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/scroll-teams-portrait-1.webp",
            featureVideo:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_Jira_LowBR.mp4",
            posterImage:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_Jira-4003614779.webp",
            accentImg:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/scroll-teams-accent-1.webp",
            bgColor: "FFA900",
            bgShape:
                "0% 35%, 1% 98%, 57% 100%, 57% 96%, 100% 97%, 100% 0%, 54% 2%, 54% 7%, 53% 17%"
        },
        {
            productName: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Confluence.ProductName",
                defaultMessage: "Confluence"
            }),
            logo: <LogoWhiteConfluence />,
            cta: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Confluence.Cta",
                defaultMessage: "Get started with Confluence"
            }),
            ctaLink: `${useLocalizedHref("/software/confluence")}`,
            ctaLabel: "Get started with Confluence",
            header: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Confluence.Header",
                defaultMessage: "Scale your knowledge"
            }),
            description: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Confluence.Description",
                defaultMessage:
                    "Connect and consolidate scattered docs and disconnected teammates in one, central source of truth."
            }),
            quote: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Confluence.Quote",
                defaultMessage:
                    "Confluence is our linchpin for everything. 'How do I set up my dev box?' 'How do I get my work going?' Whatever they want to know, it's all there"
            }),
            name: "Evan Lerer",
            // TODO: localize title
            title: "Director of Engineering, Redfin",
            brand: "https://wac-cdn.atlassian.com/misc-assets/webp-images/scroll-teams-brand-2.webp",
            portrait:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/scroll-teams-portrait-2.webp",
            featureVideo:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_ Confluence_LowBR.mp4",
            posterImage:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_ Confluence-2040505915.webp",
            accentImg:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/scroll-teams-accent-4.webp",

            bgColor: "BF63F3",
            bgShape:
                "6% 28%, 52% 23%, 53% 5%, 100% 0%, 100% 99%, 6% 96%, 6% 89%, 0% 88%, 0% 51%, 7% 50%"
        },
        {
            productName: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JSM.ProductName",
                defaultMessage: "Jira Service Management"
            }),
            logo: <LogoWhiteJiraServiceManagement />,
            cta: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JSM.Cta",
                defaultMessage: "Get started with Jira Service Management"
            }),
            ctaLink: `${useLocalizedHref("/software/jira/service-management")}`,
            ctaLabel: "Get started with Jira Service Management",
            header: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JSM.Header",
                defaultMessage: "Deliver service faster"
            }),
            description: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JSM.Description",
                defaultMessage:
                    "Connect Dev and Ops teams on a single platform with customizable features and AI-powered agents."
            }),
            quote: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JSM.Quote",
                defaultMessage:
                    "Atlassian Intelligence acts a virtual teammate summarising 5-page PIR reports into 5-sentence recaps for me... It's already proven its worth by improving productivity across our product teams."
            }),
            name: "Matthias Hansen",
            title: "Group CTO, Domino's Pizza Enterprises",
            brand: "https://wac-cdn.atlassian.com/misc-assets/webp-images/scroll-teams-brand-3.webp",
            portrait:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/scroll-teams-portrait-3.webp",
            featureVideo:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_ JSM_LowBR.mp4",
            posterImage:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_ JSM-3020245023.webp",
            accentImg:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/scroll-teams-accent-2.webp",

            bgColor: "82B536",
            bgShape:
                "0% 29%, 42% 28%, 43% 8%, 100% 1%, 100% 89%, 51% 98%, 50% 89%, 2% 88%, 2% 88%, 2% 88%"
        },
        {
            productName: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Loom.ProductName",
                defaultMessage: "Loom"
            }),
            logo: <LogoWhiteLoom style={{ backgroundColor: "#625df5" }} />,
            cta: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Loom.Cta",
                defaultMessage: "Get started with Loom"
            }),
            ctaLink: "https://www.loom.com/",
            ctaLabel: "Get started with Loom",
            header: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Loom.Header",
                defaultMessage: "Record, share, collaborate"
            }),
            description: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Loom.Description",
                defaultMessage:
                    "Easily record and share AI-powered video messages to reach a broader audience for better async collaboration."
            }),
            quote: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.Loom.Quote",
                defaultMessage:
                    "'Loom has been the light of my life since you showed me it.' — I never tire of hearing this from folks."
            }),
            name: "Alexis Ohanian",
            // TODO: localize title
            title: "Founder, Seven Seven Six",
            brand: "https://wac-cdn.atlassian.com/misc-assets/webp-images/scroll-teams-brand-4.webp",
            portrait:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/scroll-teams-portrait-4.webp",
            featureVideo:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_ Loom_LowBR.mp4",
            posterImage:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_ Loom-791474346.webp",
            accentImg:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/scroll-teams-accent-3.webp",

            bgColor: "FFF",
            bgShape:
                "0% 29%, 42% 28%, 43% 5%, 100% 0%, 100% 95%, 60% 95%, 60% 100%, 2% 93%, 2% 93%, 2% 93%"
        },
        {
            productName: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JPD.ProductName",
                defaultMessage: "Jira Product Discovery"
            }),
            logo: <LogoWhiteJiraProductDiscovery />,
            cta: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JPD.Cta",
                defaultMessage: "Get started with Jira Product Discovery"
            }),
            ctaLink: `${useLocalizedHref("/software/jira/product-discovery")}`,
            ctaLabel: "Get started with Jira Product Discovery",
            header: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JPD.Header",
                defaultMessage: "Ideate faster than ever"
            }),
            description: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JPD.Description",
                defaultMessage:
                    "Capture and prioritize ideas and align everyone with product roadmaps - all in one single Jira platform."
            }),
            quote: intl.formatMessage({
                id: "HomePage.ScrollPinnedSection.JPD.quote",
                defaultMessage:
                    "Jira Product Discovery is helping us do the right thins, in the right oder, and at the right time, focusing on work that will deliver on benefits."
            }),
            name: "Paul Leon",
            // TODO: localize title
            title: "Digital Portfolio Director, Inchcape",
            brand: "https://wac-cdn.atlassian.com/misc-assets/webp-images/scroll-teams-brand-5.webp",
            portrait:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/scroll-teams-portrait-5.webp",
            featureVideo:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_ JPD_LowBR.mp4",
            posterImage:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_ JPD-286968629.webp",
            accentImg:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/scroll-teams-accent-5.webp",
            bgColor: "09326C",
            bgShape:
                "0% 25%, 42% 20%, 42% 5%, 100% 0%, 100% 95%, 51% 100%, 50% 94%, 3% 95%, 3% 95%, 3% 95%"
        }
    ];
    const { ref: scrollRef, inView: sectionIsVisible } = useInView({
        threshold: 0.15
    });
    const [activeAccentImg, setActiveAccentImg] = useState<string>("");
    const [activeBrand, setActiveBrand] = useState<string>("");
    const [activeColor, setActiveColor] = useState<string>("");
    const [activeCTA, setActiveCTA] = useState<string>("");
    const [activeCTALink, setActiveCTALink] = useState<string>("");
    const [activeCTALabel, setActiveCTALabel] = useState<string>("");
    const [activeFeatureVideo, setActiveFeatureVideo] = useState<string>("");
    const [activePosterImage, setActivePosterImage] = useState<string>("");
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const [activeShape, setActiveShape] = useState<string | null>("");

    return (
        <section>
            <ScrollContext.Provider
                value={{
                    activeAccentImg,
                    activeBrand,
                    activeColor,
                    activeCTA,
                    activeCTALink,
                    activeCTALabel,
                    activeFeatureVideo,
                    activePosterImage,
                    activeIndex,
                    activeShape,
                    setActiveAccentImg,
                    setActiveBrand,
                    setActiveColor,
                    setActiveCTA,
                    setActiveCTALink,
                    setActiveCTALabel,
                    setActiveFeatureVideo,
                    setActivePosterImage,
                    setActiveIndex,
                    setActiveShape
                }}
            >
                <UFOSegment name="homepage-v2-scrollPinnedSection">
                    <div
                        css={scrollerStyles}
                        ref={scrollRef}
                        className={sectionIsVisible ? "is-visible" : "hidden"}
                    >
                        <Row>
                            <div className="row-width">
                                <div className="header-wrapper">
                                    <div
                                        css={css({
                                            insetBlockStart: "0",
                                            display: "flex",
                                            flexDirection: "column"
                                        })}
                                    >
                                        <div className="accent-heading">
                                            <h2>
                                                <Sparkles />
                                                {intl.formatMessage({
                                                    id: "HomePage.ScrollPinnedSectionHeader",
                                                    defaultMessage:
                                                        "Teamwork solutions for high-performing teams"
                                                })}
                                            </h2>
                                        </div>
                                        <div
                                            className="half img-section"
                                            css={css({
                                                width: "calc(60% - 30px)",
                                                alignSelf: "flex-end",
                                                minHeight: 0
                                            })}
                                        >
                                            <div className="img-region">
                                                <div
                                                    className={`bg-shape ${
                                                        activeIndex % 2 == 0
                                                            ? "even"
                                                            : "odd"
                                                    }`}
                                                    style={{
                                                        clipPath: `polygon(${activeShape})`,
                                                        backgroundColor: `#${activeColor}`
                                                    }}
                                                />
                                                <img
                                                    src={activeAccentImg}
                                                    className={`img-accent ${
                                                        activeIndex % 2 == 0
                                                            ? "even"
                                                            : "odd"
                                                    }`}
                                                    alt={""}
                                                />

                                                <Video
                                                    videoLink={
                                                        activeFeatureVideo
                                                    }
                                                    videoType="mp4"
                                                    className={`img-screenshot ${
                                                        activeIndex % 2 == 0
                                                            ? "even"
                                                            : "odd"
                                                    }`}
                                                    videoPreviewImage={
                                                        activePosterImage
                                                    }
                                                    aspectRatio="16/9"
                                                />
                                            </div>
                                            {activeCTALink && (
                                                <Adg4Button
                                                    variant="primary"
                                                    color="blue"
                                                    href={activeCTALink}
                                                    dataEventProps={{
                                                        "data-product-key":
                                                            "home",
                                                        "data-event": "clicked",
                                                        "data-event-component":
                                                            "linkButton",
                                                        "data-event-container":
                                                            "ctaSection",
                                                        "data-label": activeCTA,
                                                        "data-label-english":
                                                            activeCTALabel
                                                    }}
                                                >
                                                    {activeCTA}
                                                </Adg4Button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="half text-section">
                                    {Teams.map(
                                        (team: TeamType, index: number) => (
                                            <HomepageSectionScrollItem
                                                key={index}
                                                index={index}
                                                brand={team.brand}
                                                quote={team.quote}
                                                productName={team.productName}
                                                logo={team.logo}
                                                cta={team.cta}
                                                ctaLink={team.ctaLink}
                                                ctaLabel={team.ctaLabel}
                                                header={team.header}
                                                name={team.name}
                                                title={team.title}
                                                description={team.description}
                                                portrait={team.portrait}
                                                bgColor={team.bgColor}
                                                bgShape={team.bgShape}
                                                accentImg={team.accentImg}
                                                featureVideo={team.featureVideo}
                                                posterImage={team.posterImage}
                                            />
                                        )
                                    )}
                                </div>
                            </div>
                        </Row>
                    </div>
                </UFOSegment>
            </ScrollContext.Provider>
        </section>
    );
};
const scrollerStyles = css({
    "@keyframes imagefadein": {
        "0%": {
            opacity: 0
        },
        "100%": {
            opacity: 1
        }
    },
    "@keyframes bgshapepulseodd": {
        "0%": {
            transform: "scale(1)"
        },
        "50%": {
            transform: "scale(0.9)"
        },
        "100%": {
            transform: "scale(1)"
        }
    },
    "@keyframes bgshapepulseeven": {
        "0%": {
            transform: "scale(1)"
        },
        "50%": {
            transform: "scale(0.9)"
        },
        "100%": {
            transform: "scale(1)"
        }
    },
    "@property --background-color-1": {
        syntax: "<color>",
        initialValue: "#fff",
        inherits: "false"
    },
    "--background-color-1": "#fff",
    "transition": "background-color 1s",
    "position": "relative",
    "margin": "80px 0 115px",
    "backgroundColor": "var(--background-color-1)",
    "backgroundImage":
        "linear-gradient(to bottom, white 40px, transparent 50px), linear-gradient(to top, white 100px, transparent 200px)",
    ".row-width": {
        "position": "relative",
        [media.below.sm]: {
            "flexDirection": "column",
            ".half": {
                "&.text-section": {
                    ".team-type": {
                        marginBlockEnd: "100px"
                    },
                    ".team-card": {
                        opacity: 1
                    },
                    ".img-region": {
                        display: "block",
                        margin: "50px auto 40px"
                    },
                    ".cta-button": {
                        display: "block",
                        margin: "0 20px"
                    }
                },
                "&.img-section": {
                    display: "none"
                }
            }
        },
        [media.above.sm]: {
            ".header-wrapper": {
                position: "sticky",
                insetBlockStart: "0",
                zIndex: "2"
            }
        },
        ".accent-heading": {
            background:
                "linear-gradient(180deg, var(--background-color-1) 90%, rgba(255,255,255, 0))",
            paddingBlockStart: "5vh",
            paddingBlockEnd: "30px",
            h2: {
                fontSize: "2.5rem",
                position: "relative",
                svg: {
                    position: "absolute",
                    insetInlineStart: "60%",
                    insetBlockStart: "-60px"
                }
            }
        },
        ".half": {
            [media.above.sm]: {
                "&.text-section": {
                    "width": "calc(40% - 30px)",
                    "position": "relative",
                    "insetBlockStart": "-200px",
                    ".img-region, .cta-button": {
                        display: "none"
                    }
                },
                "&.img-section": {
                    aspectRatio: "13/10",
                    alignItems: "flex-end",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    flexBasis: "60%",
                    position: "sticky",
                    insetBlockStart: "0",
                    paddingBlockStart: "40px"
                },
                ".team-type": {
                    position: "relative",
                    minHeight: "800px"
                }
            },
            ".img-region": {
                "position": "relative",
                "marginBlockEnd": "40px",
                "aspectRatio": "7/4",
                "padding": "20px",
                "width": "100%",
                ".bg-shape": {
                    "width": "100%",
                    "height": "100%",
                    "transition": "all 1s",
                    "position": "absolute",
                    "insetInlineStart": 0,
                    "insetBlockStart": 0,
                    "&.odd": {
                        animation: "bgshapepulseodd ease-in-out 1s 1"
                    },
                    "&.even": {
                        animation: "bgshapepulseeven ease-in-out 1s 1"
                    }
                },
                "img, video": {
                    "&.img-screenshot": {
                        position: "relative",
                        display: "block",
                        height: "auto",
                        width: "100%",
                        margin: "auto",
                        animation: "imagefadein 0.25s 1"
                    },
                    "&.img-accent": {
                        width: "20%",
                        position: "absolute",
                        insetInlineStart: "-2%",
                        insetBlockEnd: "-6%"
                    }
                }
            }
        }
    }
});
